import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icons } from './ui/Icons';
import { AppHeader } from './ui/AppHeader';
import { UserProfile, Organization, NotificationSettings } from '../types';
import { updateOrganization } from '../services/firebase';

interface NotificationSettingsAdminProps {
  user: UserProfile;
  organization: Organization | null;
  onLogout: () => void;
}

interface SettingToggleProps {
  id: string;
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

const SettingToggle: React.FC<SettingToggleProps> = ({
  id,
  label,
  description,
  checked,
  onChange,
  disabled
}) => (
  <div className="flex items-center justify-between py-4 border-b border-gray-100 last:border-b-0">
    <div className="flex-1 pr-4">
      <label htmlFor={id} className="font-medium text-gray-900 cursor-pointer">
        {label}
      </label>
      <p className="text-sm text-gray-500 mt-1">{description}</p>
    </div>
    <button
      id={id}
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${checked ? 'bg-primary' : 'bg-gray-200'}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  </div>
);

const defaultSettings: NotificationSettings = {
  welcomeEmail: true,
  studyCompletionEmail: false,
  courseCompletionEmail: false,
  newStudyPublishedEmail: false,
  questionNotificationEmail: true,
  replyNotificationEmail: true,
  ctaResponseEmail: true,
};

export const NotificationSettingsAdmin: React.FC<NotificationSettingsAdminProps> = ({
  user,
  organization,
  onLogout
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('admin');
  const [settings, setSettings] = useState<NotificationSettings>(defaultSettings);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Load settings from organization on mount
  useEffect(() => {
    if (organization?.notificationSettings) {
      setSettings({
        ...defaultSettings,
        ...organization.notificationSettings
      });
    }
  }, [organization]);

  const handleSettingChange = (key: keyof NotificationSettings, value: boolean) => {
    setSettings(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
    setSaveSuccess(false);
  };

  const handleSave = async () => {
    if (!organization) return;

    try {
      setSaving(true);
      setError(null);
      await updateOrganization(organization.id, {
        notificationSettings: settings
      });
      setSaveSuccess(true);
      setHasChanges(false);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      console.error('Failed to save notification settings:', err);
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-500">No organization found</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <AppHeader
        user={user}
        organization={organization}
        onLogout={onLogout}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {/* Page Content */}
      <div className="max-w-3xl mx-auto px-6 py-8">
        {/* Back Button */}
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-6"
        >
          <Icons.ChevronLeft className="w-5 h-5" />
          Back to Dashboard
        </button>

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <Icons.Mail className="w-8 h-8 text-primary" />
            <h1 className="text-3xl font-bold text-gray-900">Notification Settings</h1>
          </div>
          <p className="text-gray-600">
            Control email notifications for members of {organization.name}
          </p>
        </div>

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-red-800">
              <Icons.X className="w-5 h-5" />
              <span className="font-medium">Error saving settings</span>
            </div>
            <p className="text-red-700 text-sm mt-1">{error}</p>
          </div>
        )}

        {/* Success State */}
        {saveSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2 text-green-800">
              <Icons.Check className="w-5 h-5" />
              <span className="font-medium">Settings saved successfully!</span>
            </div>
          </div>
        )}

        {/* Settings Card */}
        <div className="bg-white rounded-lg shadow-md">
          {/* Member Notifications Section */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Member Notifications</h2>
            <p className="text-sm text-gray-500 mb-4">Emails sent to organization members</p>

            <SettingToggle
              id="welcomeEmail"
              label="Welcome Email"
              description="Send a welcome email when a new user joins your organization"
              checked={settings.welcomeEmail ?? true}
              onChange={(v) => handleSettingChange('welcomeEmail', v)}
              disabled={saving}
            />

            <SettingToggle
              id="studyCompletionEmail"
              label="Study Completion Email"
              description="Congratulate members when they complete a study"
              checked={settings.studyCompletionEmail ?? false}
              onChange={(v) => handleSettingChange('studyCompletionEmail', v)}
              disabled={saving}
            />

            <SettingToggle
              id="courseCompletionEmail"
              label="Course Completion Email"
              description="Congratulate members when they complete an entire course"
              checked={settings.courseCompletionEmail ?? false}
              onChange={(v) => handleSettingChange('courseCompletionEmail', v)}
              disabled={saving}
            />

            <SettingToggle
              id="newStudyPublishedEmail"
              label="New Study Published"
              description="Notify all members when a new study is published in your organization"
              checked={settings.newStudyPublishedEmail ?? false}
              onChange={(v) => handleSettingChange('newStudyPublishedEmail', v)}
              disabled={saving}
            />
          </div>

          {/* Q&A Notifications Section */}
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Q&A Notifications</h2>
            <p className="text-sm text-gray-500 mb-4">Emails for questions and answers</p>

            <SettingToggle
              id="questionNotificationEmail"
              label="New Question Notification"
              description="Email study authors when someone asks a question on their study"
              checked={settings.questionNotificationEmail ?? true}
              onChange={(v) => handleSettingChange('questionNotificationEmail', v)}
              disabled={saving}
            />

            <SettingToggle
              id="replyNotificationEmail"
              label="Reply Notification"
              description="Email users when an author replies to their question"
              checked={settings.replyNotificationEmail ?? true}
              onChange={(v) => handleSettingChange('replyNotificationEmail', v)}
              disabled={saving}
            />
          </div>

          {/* Admin Notifications Section */}
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Admin Notifications</h2>
            <p className="text-sm text-gray-500 mb-4">Emails sent to organization admins</p>

            <SettingToggle
              id="ctaResponseEmail"
              label="Call to Action Responses"
              description="Email admins when someone submits a Call to Action form (email signup, free book request, event RSVP, etc.)"
              checked={settings.ctaResponseEmail ?? true}
              onChange={(v) => handleSettingChange('ctaResponseEmail', v)}
              disabled={saving}
            />
          </div>

          {/* Save Button */}
          <div className="px-6 py-4 bg-gray-50 rounded-b-lg border-t border-gray-200">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                {hasChanges ? 'You have unsaved changes' : 'All changes saved'}
              </p>
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                className={`px-6 py-2 rounded-lg font-medium text-white transition-colors flex items-center gap-2 ${
                  saving || !hasChanges
                    ? 'bg-gray-300 cursor-not-allowed'
                    : 'bg-primary hover:bg-primary/90'
                }`}
              >
                {saving ? (
                  <>
                    <Icons.Loader className="w-4 h-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Icons.Save className="w-4 h-4" />
                    Save Settings
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <Icons.Quiz className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">About Email Notifications</h3>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>Emails are sent to members' registered email addresses</li>
                <li>These settings apply to all members of your organization</li>
                <li>Some notifications (like password resets) are always sent regardless of settings</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
